import { useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { CodeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { tenantApi } from '../api';
import { useAsync } from '../components/useAsync';
import PageShell from '../components/PageShell';
import DataTable from '../components/DataTable';
import type { Tenant, TenantConfigDraft, TenantDetail } from '../types';

/**
 * Tenants are read-only here, on purpose.
 *
 * `tenants_config/*.json` is git-owned and seiki-backend replays it over the
 * `tenants` table on every boot (app/services/common/tenant_sync.py), so an edit
 * form that wrote the DB would be silently reverted by the next deploy. Instead
 * this screen renders the current state and generates a validated config file to
 * open a PR with — the change path that actually holds.
 */
export default function TenantsPage() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [detailId, setDetailId] = useState<number | null>(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftSeed, setDraftSeed] = useState<TenantDetail | null>(null);
  // The key of the draft modal, bumped on open and never on close, so every
  // open gets a fresh form store: antd's store outlives the mounted <Form> and
  // `initialValues` only fills the keys it does not already hold, so a reused
  // instance carried the previously seeded tenant's fields — into a second
  // tenant, and into an unseeded fresh draft wholesale.
  const [draftOpens, setDraftOpens] = useState(0);

  function openDraft(seed: TenantDetail | null) {
    setDraftSeed(seed);
    setDraftOpen(true);
    setDraftOpens((n) => n + 1);
  }

  const tenants = useAsync(() => tenantApi.list({ per_page: 100, q: query || undefined }), [query]);

  const columns: ColumnsType<Tenant> = [
    {
      title: 'Subdomain',
      key: 'subdomain',
      render: (_, row) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => setDetailId(row.id)}>
          <span className="mono">{row.subdomain}</span>
        </Button>
      ),
    },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Organization',
      key: 'org',
      render: (_, row) =>
        row.org_name ?? <Tag color="orange">Not attached</Tag>,
    },
    // No country column (AD-32) and no currency column (DL-14). The tenant
    // holds neither: which markets the customer may use is
    // `organizations.allowed_countries`, and a workspace's country and
    // currencies are set on the Workspaces screen. A whitelabel running one
    // workspace per country had no single default to give for either.
    {
      title: 'Workspaces',
      dataIndex: 'workspace_count',
      key: 'workspace_count',
      width: 110,
      align: 'center',
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag>,
    },
  ];

  return (
    <PageShell
      title="Tenants"
      subtitle="Whitelabel configuration: branding, features and lingo."
      actions={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={tenants.reload}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<CodeOutlined />}
            onClick={() => {
              openDraft(null);
            }}
          >
            Draft a config file
          </Button>
        </Space>
      }
      notice={
        <Alert
          type="info"
          showIcon
          message="Tenants are managed in git, not in this console"
          description={
            <>
              seiki-backend rebuilds the <span className="mono">tenants</span> table from{' '}
              <span className="mono">tenants_config/*.json</span> on every app start, so editing
              the row directly would be undone by the next deploy. Use{' '}
              <strong>Draft a config file</strong> to produce a validated JSON file, then open a
              PR against <span className="mono">seiki-backend</span>. Attaching a tenant to an
              organization is a normal change and lives on the Organizations screen.
            </>
          }
        />
      }
      toolbar={
        <>
          <Input
            placeholder="Search subdomain or name"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => setQuery(search.trim())}
            style={{ width: 260 }}
            allowClear
            onClear={() => {
              setSearch('');
              setQuery('');
            }}
          />
          <Button onClick={() => setQuery(search.trim())}>Search</Button>
        </>
      }
    >
      <DataTable<Tenant>
        minWidth={1120}
        rowKey="id"
        loading={tenants.loading}
        columns={columns}
        dataSource={tenants.data?.items ?? []}
        pagination={false}
      />

      <TenantDetailDrawer
        tenantId={detailId}
        onClose={() => setDetailId(null)}
        onEditAsDraft={(detail) => {
          setDetailId(null);
          openDraft(detail);
        }}
      />
      <ConfigDraftModal
        key={`draft-${draftOpens}`}
        open={draftOpen}
        seed={draftSeed}
        onClose={() => {
          setDraftOpen(false);
          setDraftSeed(null);
        }}
      />
    </PageShell>
  );
}

function TenantDetailDrawer({
  tenantId,
  onClose,
  onEditAsDraft,
}: {
  tenantId: number | null;
  onClose: () => void;
  onEditAsDraft: (detail: TenantDetail) => void;
}) {
  const detail = useAsync<TenantDetail | null>(
    () => (tenantId ? tenantApi.get(tenantId) : Promise.resolve(null)),
    [tenantId],
  );
  const tenant = detail.data;

  return (
    <Drawer
      title={tenant ? `${tenant.subdomain} — ${tenant.name}` : 'Tenant'}
      width={620}
      open={tenantId !== null}
      onClose={onClose}
      destroyOnClose
      loading={detail.loading}
      extra={
        tenant && (
          <Button size="small" onClick={() => onEditAsDraft(tenant)}>
            Edit as config draft
          </Button>
        )
      }
    >
      {tenant && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="Owned by git"
            description={
              <>
                This row is rebuilt from <span className="mono">{tenant.config_path}</span> at
                every app start.
              </>
            }
          />
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Organization">
              {tenant.org_name ?? 'Not attached'}
            </Descriptions.Item>
            <Descriptions.Item label="Active">
              <Tag color={tenant.is_active ? 'green' : 'red'}>
                {tenant.is_active ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Primary colour">
              {tenant.primary_color ? (
                <Space>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      border: '1px solid #d1d5db',
                      background: tenant.primary_color,
                      verticalAlign: 'middle',
                    }}
                  />
                  <span className="mono">{tenant.primary_color}</span>
                </Space>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Logo, favicon">
              {/* AD-141: branding images belong to the organization now. */}
              {tenant.org_name
                ? `Set on the organization ${tenant.org_name} (Organizations screen, Branding)`
                : 'Set on the organization; this tenant has none attached, so it shows Seiki’s'}
            </Descriptions.Item>
            <Descriptions.Item label="Timezone">{tenant.timezone ?? '—'}</Descriptions.Item>
          </Descriptions>

          <div>
            <Typography.Title level={5}>Features</Typography.Title>
            <pre className="mono" style={preStyle}>
              {JSON.stringify(tenant.features ?? {}, null, 2)}
            </pre>
          </div>
          <div>
            <Typography.Title level={5}>Lingo</Typography.Title>
            <pre className="mono" style={preStyle}>
              {JSON.stringify(tenant.lingo ?? {}, null, 2)}
            </pre>
          </div>
        </Space>
      )}
    </Drawer>
  );
}

const preStyle: React.CSSProperties = {
  background: '#f6f7f9',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  padding: 12,
  margin: 0,
  overflowX: 'auto',
};

function ConfigDraftModal({
  open,
  seed,
  onClose,
}: {
  open: boolean;
  seed: TenantDetail | null;
  onClose: () => void;
}) {
  const [form] = Form.useForm();
  const [draft, setDraft] = useState<TenantConfigDraft | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // JSON blobs are edited as text: they're free-form by schema, and a
  // structured editor would have to guess at keys the backend never constrains.
  const initialValues = seed
    ? {
        subdomain: seed.subdomain,
        name: seed.name,
        primary_color: seed.primary_color ?? undefined,
        timezone: seed.timezone ?? undefined,
        features: seed.features ? JSON.stringify(seed.features, null, 2) : undefined,
        lingo: seed.lingo ? JSON.stringify(seed.lingo, null, 2) : undefined,
      }
    : {};

  function parseJson(value: string | undefined, field: string): Record<string, unknown> | undefined {
    if (!value?.trim()) return undefined;
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('not an object');
      }
      return parsed as Record<string, unknown>;
    } catch {
      throw new Error(`${field} must be a JSON object`);
    }
  }

  async function onGenerate() {
    const values = await form.validateFields();
    setSubmitting(true);
    setDraft(null);
    try {
      const body: Record<string, unknown> = {
        subdomain: values.subdomain.trim(),
        name: values.name.trim(),
        primary_color: values.primary_color?.trim() || undefined,
        timezone: values.timezone?.trim() || undefined,
        features: parseJson(values.features, 'Features'),
        lingo: parseJson(values.lingo, 'Lingo'),
      };
      // Drop undefined keys: the backend forbids unknown fields and treats an
      // absent key as "unset", which is what an empty input means here.
      Object.keys(body).forEach((key) => body[key] === undefined && delete body[key]);

      setDraft(await tenantApi.draftConfig(body));
    } catch (err) {
      const text = (err as Error).message;
      // Local JSON parse failures never reach the API, so they need their own toast.
      if (/must be a JSON object/.test(text)) message.error(text);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={seed ? `Config draft for ${seed.subdomain}` : 'Draft a tenant config file'}
      open={open}
      onCancel={() => {
        setDraft(null);
        onClose();
      }}
      onOk={onGenerate}
      okText={draft ? 'Regenerate' : 'Generate file'}
      confirmLoading={submitting}
      width={700}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="This writes nothing"
        description="The form is validated against the same schema the startup sync uses, so a generated file is guaranteed to load. Commit it to seiki-backend to apply the change."
      />
      <Form form={form} layout="vertical" requiredMark={false} initialValues={initialValues}>
        <Form.Item
          name="subdomain"
          label="Subdomain"
          rules={[{ required: true, message: 'Subdomain is required' }]}
          extra="Lower-cased automatically. 'admin', 'api', 'app', 'www', 'seiki' and 'localhost' are reserved."
        >
          <Input placeholder="acme" disabled={!!seed} />
        </Form.Item>
        <Form.Item
          name="name"
          label="Display name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input placeholder="Acme Media" />
        </Form.Item>
        <Form.Item name="primary_color" label="Primary colour" extra="Hex, e.g. #0A2540">
          <Input placeholder="#0A2540" />
        </Form.Item>
        {/*
          No country field (AD-32) and no currency fields (DL-14). A tenant is
          branding now: which markets a customer may use is `allowed_countries`
          on their organization, and a workspace's country and currencies are
          set on the Workspaces screen.
        */}
        <Form.Item name="timezone" label="Timezone" extra="IANA name, e.g. Europe/Paris">
          <Input placeholder="Europe/Paris" />
        </Form.Item>
        <Form.Item name="features" label="Features" extra="JSON object">
          <Input.TextArea rows={4} placeholder={'{\n  "media_plans": true\n}'} />
        </Form.Item>
        <Form.Item name="lingo" label="Lingo" extra="JSON object of term overrides">
          <Input.TextArea rows={4} placeholder={'{\n  "billboard": "panel"\n}'} />
        </Form.Item>
      </Form>

      {draft && (
        <div>
          <Typography.Title level={5}>
            {draft.is_new_tenant ? 'New tenant' : 'Existing tenant'} —{' '}
            <span className="mono">{draft.config_path}</span>
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
            {draft.is_new_tenant
              ? 'No tenant row exists for this subdomain yet; merging this file creates one.'
              : 'A tenant row already exists for this subdomain; merging this file updates it.'}
          </Typography.Paragraph>
          <pre className="mono" style={preStyle}>
            {draft.file_contents}
          </pre>
          <Button
            style={{ marginTop: 12 }}
            onClick={() => {
              navigator.clipboard
                .writeText(draft.file_contents)
                .then(() => message.success('Copied to clipboard'))
                .catch(() => message.error('Could not copy — select the text manually'));
            }}
          >
            Copy file contents
          </Button>
        </div>
      )}
    </Modal>
  );
}

